import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import ErrorBoundary from 'react-error-boundary';
import isIterable from './isIterable';

const FallbackComponent = ({ componentStack, error }) => <div>Error</div>;

function getHostElements(target) {
  if (typeof target === 'string') {
    return [...document.querySelectorAll(target)];
  }

  if (isIterable(target)) {
    return [...target];
  }

  return [target];
}

function collectPropsFromElement(element, defaultProps) {
  const props = { ...defaultProps };

  Object.entries(element.dataset).forEach(([key, value]) => {
    if (key.includes('prop')) {
      const propName = key
        .replace('prop', '')
        .replace(/^\w/, c => c.toLowerCase());

      props[propName] = value;
    } else {
      props[key] = value;
    }

    // Convert true/false to booleans
    if (value === 'true') {
      props[key] = true;
    } else if (value === 'false') {
      props[key] = false;
    }
  });

  [...element.getElementsByTagName('script')].forEach(script => {
    let propsObj = {};

    if (
      script.hasAttribute('type') &&
      script.getAttribute('type') === 'text/props'
    ) {
      try {
        propsObj = JSON.parse(script.innerHTML);
      } catch (e) {
        throw new Error(e);
      }
      Object.assign(props, propsObj);
    }
  });

  return props;
}

function getDisplayName(Component) {
  return Component.displayName || Component.name || 'Unknown';
}

function isEditingMode() {
  return document.body.classList.contains('sv-editing-mode');
}

function getQuerySelector(element) {
  return `.${element.className.split(' ').join('.')}`;
}

function getReactElement(element) {
  if (!element || !element.className || !element.parentElement) return null;
  const selector = getQuerySelector(element);
  return document.querySelector(selector);
}

function onEditModeLoad(rootElement, element) {
  if (!window.clientBridge) return;
  window.clientBridge.on('content', 'updated', () => {
    const reactElement = getReactElement(element);
    if (!reactElement) return;

    ReactDOM.unmountComponentAtNode(element);
    ReactDOM.render(rootElement, reactElement);
  });
}

function renderWidget({ widget, element, defaultProps }) {
  const props = collectPropsFromElement(element, defaultProps);
  const widgetElement = React.createElement(widget, props);

  function Root() {
    return (
      <StrictMode>
        <ErrorBoundary FallbackComponent={FallbackComponent}>
          {widgetElement}
        </ErrorBoundary>
      </StrictMode>
    );
  }

  Root.displayName = `Widget(${getDisplayName(widget)})`;
  const rootElement = React.createElement(Root);

  ReactDOM.render(rootElement, element);

  if (isEditingMode()) {
    document.addEventListener('DOMContentLoaded', () =>
      onEditModeLoad(rootElement, element),
    );
  }
}

export default function createWidget(widget) {
  function render(target, { defaultProps = {} } = {}) {
    if (!target) return;
    const elements = getHostElements(target);

    elements.forEach(element => {
      renderWidget({ widget, element, defaultProps });
    });
  }

  return { render };
}
